import type { ReactNode } from 'react';
import SignalWordmark from '@/components/icons/SignalWordmark';
import { cn } from '@/lib/utils';

// AUT-39: decisions/auth-screens-share-one-layout.md -- the chrome for all
// four auth screens (/login, /forgot-password, /reset-password, /signup),
// consolidated out of three separate inline copies. `variant` decides
// whether the gradient hero panel renders at all -- a structural choice,
// not a class list, which is why this is a prop branch and not CVA.
export type AuthLayoutVariant = 'hero' | 'centered';

export interface AuthLayoutProps {
  variant: AuthLayoutVariant;
  children: ReactNode;
}

// WEB-007: values read directly off the design prototype
// (`https://preview--signal-v3.lovable.app/login`, computed styles,
// 2026-08-17) -- conventions/03-frontend-structure.md's named "login
// gradient" exception. Stop order and colours are unchanged from WEB-001;
// only the last two stop positions move to 72% and 100%, matching the
// prototype exactly.
const HERO_GRADIENT =
  'linear-gradient(to bottom, #342799 0%, #4C32F2 33%, #A242A2 72%, #FF534C 100%)';

/**
 * The shared chrome for all four auth screens. Owns the background, the
 * hero panel and its footer line -- nothing else. Each page still owns its
 * own heading, sub copy and form via `children`.
 *
 * Renders no navigation of any kind (decisions/auth-screens-share-one-layout.md
 * § Decision): no link, no back arrow, no clickable logo.
 */
export const AuthLayout = ({ variant, children }: AuthLayoutProps) => {
  return (
    <div
      className={cn('relative grid min-h-screen w-full bg-background', variant === 'hero' && 'lg:grid-cols-2')}
    >
      {variant === 'hero' && (
        <div
          className="relative isolate hidden items-center justify-center overflow-hidden p-12 lg:flex"
          style={{ backgroundImage: HERO_GRADIENT }}
        >
          {/* The translucent "R" mark, centred and behind the copy.
              2026-08-19: `opacity-[0.04]`, not the `opacity-[0.07]` the
              prototype's class carries. The prototype draws the mark as an
              `<img>` whose asset is itself partly transparent, so its class
              value is not its rendered alpha; ours is an inline `<svg>` with
              `fill="white"`, so the class value *is* -- WEB-007 copied the
              number across that difference and the mark shipped twice as
              bright. Measured off `design-input/signal-v3-auth/login.jpg`
              against the gradient behind it: the capture reads 0.037 in the
              rectangle and 0.032 in the quarter-circle, `0.07` rendered
              0.068, and this value renders 0.041 / 0.030. Change it by
              measuring, not by reading a class off the prototype. */}
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 flex items-center justify-center">
            <svg viewBox="0 0 46 46" className="w-[80%] max-w-none opacity-[0.04]">
              <path
                d="M33.7972 22.5314H22.5315C22.5315 34.9865 32.6078 45.0629 45.063 45.0629V33.7971C38.8042 33.7971 33.7972 28.7277 33.7972 22.5314Z"
                fill="white"
              />
              <path
                d="M22.5315 -0.00012207H11.2657H0V11.2656V22.5314H11.2657V11.2656H22.5315C28.7278 11.2656 33.7972 16.3351 33.7972 22.5314H45.063C45.063 10.0762 34.9241 -0.00012207 22.5315 -0.00012207Z"
                fill="white"
              />
            </svg>
          </div>

          <div className="absolute left-1/2 top-[28.5%] w-full max-w-[33.6rem] -translate-x-1/2 space-y-4 px-12">
            {/* WEB-008: the hero copy is the product's only <h1> -- every
                page heading below it is an <h2> (correction sheet § 1). */}
            <h1 className="text-3xl font-semibold text-sidebar-foreground">
              Search intelligence for the brands that win on Amazon.
            </h1>
            <p className="text-sm text-sidebar-foreground/70">
              Track rankings, run A/B tests, and turn keyword signals into listing decisions.
            </p>
          </div>

          <SignalWordmark className="absolute left-1/2 top-[62%] h-[2.7rem] w-auto -translate-x-1/2" />

          <p className="absolute bottom-12 left-12 text-xs text-sidebar-foreground/50">
            © Recom. All rights reserved.
          </p>
        </div>
      )}

      <div className="relative isolate flex items-center justify-center overflow-hidden bg-white p-6 sm:p-12">
        {/* The right panel's wash: four blurred circles, blue/indigo top-left
            and pink/coral bottom-right, rendered at every width. */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 bg-white">
          <div className="absolute -left-52 -top-48 h-[32.5rem] w-[32.5rem] rounded-full bg-[hsl(228_85%_88%)] opacity-95 blur-[110px]" />
          <div className="absolute -left-32 top-[3rem] h-[20.5rem] w-[20.5rem] rounded-full bg-[hsl(238_75%_90%)] opacity-80 blur-[100px]" />
          <div className="absolute -right-36 bottom-[-11rem] h-[21rem] w-[21rem] rounded-full bg-[hsl(348_92%_86%)] opacity-100 blur-[90px]" />
          <div className="absolute -right-8 bottom-[-9rem] h-[12rem] w-[12rem] rounded-full bg-[hsl(12_90%_86%)] opacity-90 blur-[80px]" />
        </div>

        <div className="w-full max-w-sm">{children}</div>
      </div>
    </div>
  );
};
